// suppress linter errors about d3 being undefined
// we require("../js/libs/d3.min.js") in app/static/frontend/index.jsx
/* global d3 */

import {
    useEffect,
    useMemo,
} from "react"

// renderers
import {
    hideTooltip,
    renderTooltip,
    showTooltip,
} from "app/static/frontend/widgets/components/visualization/renderers/sharedRenderers"

// selectors
import { selectShowInSearchPermanentFilters } from "app/static/frontend/widgets/selectors/listWidgetSelectors"

// constructs a pie layout generator function that will compute the arc angles for the svg
export const pieRenderer = ({
    data,
    timestamp,
}) =>
    useMemo(() => {
        if (data && data.length) {
            return d3
                // TODO: uncomment below and delete .layout.pie() after d3 v5 upgrade
                // .pie()

                // layout.pie() is a convenience helper for computing the
                // start and end angles of arcs that comprise a pie or donut chart
                // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#_pie
                // https://d3-wiki.readthedocs.io/zh_CN/master/Pie-Layout/#pie
                .layout.pie()

                // If comparator is specified, sets the sort order of data for the layout using the specified comparator function.
                // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#pie_sort
                // https://d3-wiki.readthedocs.io/zh_CN/master/Pie-Layout/#sort
                .sort(null)

                // Specifies how to extract a value from the associated data
                // (e.g. sets the accessor function for the pie layout to use);
                // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#pie_value
                // https://d3-wiki.readthedocs.io/zh_CN/master/Pie-Layout/#value
                .value(d => d.value)(data)
        }

        return undefined
    }, [timestamp])

// defines a function that generates the "d" attribute arc paths to be drawn in the svg
// (which will generate the actual <path /> d drawn path attribute)
// https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d
export const arcRenderer = ({
    arcLabel,
    height,
    width,
}) =>
    useMemo(() => {
        const radius = (Math.min(width, height) / 2) + (arcLabel ? 1 : 0)

        return d3
            // TODO: uncomment below and delete .svg.arc() after d3 v5 upgrade
            // .arc()

            // Generates an arc for the given arguments.
            // The arguments are arbitrary; they are simply propagated to the arc generator’s accessor functions along with the this object.
            // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#_arc
            // https://d3-wiki.readthedocs.io/zh_CN/master/SVG-Shapes/#arc
            .svg.arc()

            // If radius is specified, sets the inner radius to the specified function or number and returns the arc generator.
            // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#arc_innerRadius
            // https://d3-wiki.readthedocs.io/zh_CN/master/SVG-Shapes/#arc_innerRadius
            .innerRadius(
                arcLabel
                    ? radius
                    : 0
            )

            // If radius is specified, sets the outer radius to the specified function or number and returns the arc generator.
            // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#arc_outerRadius
            // https://d3-wiki.readthedocs.io/zh_CN/master/SVG-Shapes/#arc_outerRadius

            // get radius by taking diameter and dividing by two
            .outerRadius(radius)
    }, [arcLabel, height, width])

export const renderPie = ({
    arcRendererMemo,
    arcLabelRendererMemo,
    colors,
    dataSources,
    editing,
    editForm,
    height,
    isExternal,
    pieRendererMemo,
    reference,
    showInSearch,
    timestamp,
    width,
}) =>
    useEffect(() => {
        if (timestamp && height && width) {
            d3
                .select(reference.current)
                .select(".pie")
                .remove()

            const tooltip = renderTooltip({ reference })
            const total = pieRendererMemo.reduce((acc, d) =>
                (acc + d.value), 0
            )
            const getTooltipText = ({
                text,
                value,
                total,
            }) => {
                const tooltipText = [text]

                if (total) {
                    tooltipText.push(`(${((value / total) * 100).toFixed(2)}%)`)
                }

                return tooltipText.join(" ")
            }

            // this populates the svg with the actual pie graph arcs
            d3
                .select(reference.current)
                // g
                // insert the Pie group before the Tooltip element due to svg rendering
                .insert("g", () => tooltip.node())

                .attr("class", "pie")
                .attr("data-cy", "pie")
                // sets the color of the pie graph border and space between arcs
                .attr("stroke", "white")
                .selectAll("path")
                .data(
                    // Evaluates the pie function on the specified array of values and
                    // returns an array of objects representing each datum’s arc angles
                    // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#pie
                    // https://d3-wiki.readthedocs.io/zh_CN/master/Pie-Layout/#_pie

                    // constructs an Array of Objects
                    // [
                    //     {
                    //         data: {index: 1, name: "Science", value: 52}
                    //         endAngle: 3.267256359733385
                    //         startAngle: 0
                    //         value: 52
                    //     },
                    //     ...
                    // ]
                    pieRendererMemo
                )

                // TODO: uncomment below and delete .enter().append("path") after d3 v5 upgrade
                // .join("path")
                // https://stackoverflow.com/a/55708965/6201291
                // path
                .enter()
                .append("path")

                .attr("data-cy", (d, i) => `pie-path-${i}`)
                // this is the actual svg shape of the arc in the pie graph
                // converts
                // {
                //     data: {index: 1, name: "Science", value: 52}
                //     endAngle: 3.267256359733385
                //     startAngle: 0
                //     value: 52
                // }
                // to
                // "M3.0003846579110155e-15,-49A49,49 0 1,1 -6.141328444650914,48.613620364409414L0,0Z"
                .attr("d", arcRendererMemo)
                // this fills each arc in the pie graph with its corresponding left-bounded colorBoundary value
                // from the ColorPickerField(s) redux-form in the sidebar
                .attr("fill", (d, i) => colors[i])
                .style("cursor", (editing || editForm || isExternal) ? "unset" : "pointer")
                .on("click", (d, i) => {
                    if (!editing && !editForm && !isExternal) {
                        showInSearch(
                            selectShowInSearchPermanentFilters({
                                ...(
                                    dataSources[i].additional_filters &&
                                        {
                                            filters: [dataSources[i].additional_filters]
                                        }
                                ),
                                safedSearch: dataSources[i].safed_search
                            })
                        )
                    }
                })
                // these event handlers hide and show our custom tooltip defined above
                .on("mouseover", (d) => {
                    if (!editing && !editForm) {
                        // d3v3 https://stackoverflow.com/questions/45262172/45262284#comment83459767_45262284
                        // d3v{4/5} https://stackoverflow.com/a/42667743/6201291
                        const thisArrow = d3.event.target
                        d3.select(thisArrow).style("opacity", "0.8")

                        // computes the midpoint [x, y] of the center line of the arc
                        // the midpoint is defined as (startAngle + endAngle) / 2 and (innerRadius + outerRadius) / 2
                        // https://github.com/d3/d3-shape/blob/v1.3.7/README.md#arc_centroid
                        // https://d3-wiki.readthedocs.io/zh_CN/master/SVG-Shapes/#arc_centroid

                        // ex:
                        // width, height = 241.5
                        // x, y = 7.455037389809513e-15, 121.75
                        const [x, y] = arcLabelRendererMemo.centroid(d)

                        // we need to initially populate the tooltip in order to make our
                        // .getBoundingClientRect() work below
                        showTooltip({
                            tooltip,
                            value: getTooltipText({
                                text: d.value,
                                value: d.value,
                                total,
                            }),
                            x,
                            y,
                        })

                        const tooltipY = () => {
                            // if y is placed outside of the bottom of the svg content (y >= height / 2),
                            // place it at the very bottom of the svg
                            if (y >= height / 2) {
                                // bounding height of tooltip element is 33, as declared in
                                // app/static/frontend/widgets/components/visualization/renderers/sharedRenderers.js
                                return (y - tooltip.node().getBoundingClientRect().height)
                            }
                            // if y is placed outside of the top of the svg content (y < 0),
                            // just make it 0
                            else if (y < 0) {
                                return 0
                            }

                            return y
                        }

                        showTooltip({
                            tooltip,
                            value: getTooltipText({
                                text: d.value,
                                value: d.value,
                                total,
                            }),
                            x,
                            y: tooltipY(),
                        })
                    }
                })
                .on("mouseout", () => {
                    if (!editing && !editForm) {
                        const thisArrow = d3.event.target
                        d3.select(thisArrow).style("opacity", "1")

                        hideTooltip({ tooltip })
                    }
                })

                // though we are using a custom tooltip,
                // we need the browser-native title element as well for accessibility purposes
                .append("title")

                .text(d => (
                    getTooltipText({
                        text: `${d.data.name}: ${d.data.value.toLocaleString()}`,
                        value: d.value,
                        total,
                    })
                ))
        }
    }, [timestamp, colors, height, width])
